'use client'

import { useMemo, useState } from 'react'

import * as TabsPrimitive from '@radix-ui/react-tabs'
import {
  ColumnDef,
  createColumnHelper,
  PaginationState,
} from '@tanstack/react-table'
import {
  ReadonlyURLSearchParams,
  usePathname,
  useRouter,
  useSearchParams,
} from 'next/navigation'

import { AccountSigners } from '~/app/accounts/AccountSigners'
import { AutoEarnIndicator } from '~/app/accounts/AutoEarnIndicator'
import { AutomationAccountDestination } from '~/app/accounts/AutomationAccountDestination'
import { AutomationAccountOutput } from '~/app/accounts/AutomationAccountOutput'
import { AutomationAccountProcessed } from '~/app/accounts/AutomationAccountProcessed'
import { AutomationAccountStatus } from '~/app/accounts/AutomationAccountStatus'
import { AddCardButton } from '~/app/accounts/cards/AddCardButton'
import { CardsPage } from '~/app/accounts/cards/cards-page'
import { buildCreatePath } from '~/app/accounts/create/createRoutes'
import { AccountRow } from '~/app/accounts/types'
import { useAutomations } from '~/app/automations/useAutomations'
import { FeatureEmptyState } from '~/components/FeatureEmptyState'
import { Table } from '~/components/Table'
import {
  TABLE_ACTION_COLUMN_META,
  TABLE_ACTION_COLUMN_WIDTH,
} from '~/components/TableActions'
import { AccountActionsMenu } from '~/components/account-detail/AccountActionsMenu'
import { withAuthentication } from '~/components/auth/withAuthentication'
import {
  AddressDisplay,
  Button,
  CurrencyAmount,
  PageLayout,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '~/components/component-library'
import { AUTOMATIONS_GUIDE_URL } from '~/constants/externalUrls'
import { useActiveOrg, useActiveOrgMember, useActiveOrgId } from '~/hooks/org'
import { useOrgBalances } from '~/hooks/useBalances'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { OrgSmartAccount, SmartAccountRole } from '~/types'
import { isAutoEarnEnabled } from '~/utils/vault'

const INITIAL_PAGE_SIZE = 10
const NAME_COLUMN_WIDTH = 320
const columnHelper = createColumnHelper<AccountRow>()

const ACCOUNT_TAB_VALUES = ['operating', 'automation', 'cards'] as const
type AccountTabValue = (typeof ACCOUNT_TAB_VALUES)[number]
const ACCOUNT_TABS = [
  { value: 'operating', display: 'Operating' },
  { value: 'automation', display: 'Automations' },
  { value: 'cards', display: 'Cards' },
]

const getActiveAccountTab = (
  searchParams: ReadonlyURLSearchParams,
): AccountTabValue => {
  const tabParam = searchParams.get('tab') as AccountTabValue | null
  return tabParam && ACCOUNT_TAB_VALUES.includes(tabParam)
    ? tabParam
    : 'operating'
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AccountColumn = ColumnDef<AccountRow, any>

const getAccountType = (account: OrgSmartAccount) => {
  if (account.role === SmartAccountRole.AUTOMATION) return 'Automation'

  return 'Operating'
}

const getAccountTypeRank = (account: AccountRow) => {
  if (account.type === 'Operating') return 0
  return 1
}

const sortAccountRows = (
  accounts: AccountRow[],
  balances: ReturnType<typeof useOrgBalances>['balances'],
) => {
  return [...accounts].sort((a, b) => {
    const typeDiff = getAccountTypeRank(a) - getAccountTypeRank(b)
    if (typeDiff !== 0) return typeDiff

    const valueDiff =
      (balances[b.address]?.totalValue ?? 0) -
      (balances[a.address]?.totalValue ?? 0)
    if (valueDiff !== 0) return valueDiff

    return a.name.localeCompare(b.name)
  })
}

const AccountName = ({ account }: { account: AccountRow }) => {
  return (
    <AddressDisplay address={account.address} layout="two-line" isInternal />
  )
}

const AccountBalance = ({
  account,
  smartAccountBalances,
  isLoadingSmartAccountBalances,
}: {
  account: AccountRow
  smartAccountBalances: ReturnType<typeof useOrgBalances>['balances']
  isLoadingSmartAccountBalances: boolean
}) => {
  // Operating accounts only — automation accounts render in their own tab,
  // which has no balance column.
  return (
    <div className="flex w-full items-center justify-end gap-2">
      {isAutoEarnEnabled(account.account) && <AutoEarnIndicator />}
      <CurrencyAmount
        value={smartAccountBalances[account.address]?.totalValue}
        isLoading={isLoadingSmartAccountBalances}
        layout="inline"
      />
    </div>
  )
}

const AccountRules = ({ account }: { account: AccountRow }) => {
  if (account.account.role === SmartAccountRole.AUTOMATION) {
    if (!account.automation) return null

    return <AutomationAccountOutput automation={account.automation} />
  }

  return <AccountSigners account={account.account} />
}

const accountActionsColumn = columnHelper.display({
  id: 'actions',
  ...TABLE_ACTION_COLUMN_WIDTH,
  cell: function ActionsCell(ctx) {
    return (
      <div className="flex justify-end">
        <AccountActionsMenu
          smartAccount={ctx.row.original.account}
          surface="table"
          automation={ctx.row.original.automation}
        />
      </div>
    )
  },
  meta: TABLE_ACTION_COLUMN_META,
  enableColumnFilter: false,
})

const AccountsPageInner = () => {
  const pathname = usePathname()
  const router = useRouter()
  const searchParams = useSearchParams()
  const activeTab = getActiveAccountTab(searchParams)

  const handleTabChange = (value: string) => {
    const nextSearchParams = new URLSearchParams(searchParams)
    nextSearchParams.set('tab', value)
    router.replace(`${pathname}?${nextSearchParams.toString()}`, {
      scroll: false,
    })
  }

  return (
    <TabsPrimitive.Root value={activeTab} onValueChange={handleTabChange}>
      <PageLayout
        pageType="table"
        title="Accounts"
        actionButtons={
          activeTab === 'cards' ? (
            <AddCardButton>Create account</AddCardButton>
          ) : (
            <NewAccountButton />
          )
        }
        stickyContent={
          <div className="border-b border-gray-200 pt-4 dark:border-gray-800">
            <TabsList variant="table">
              {ACCOUNT_TABS.map(({ value, display }) => (
                <TabsTrigger key={value} value={value}>
                  {display}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        }
      >
        {/* w-full (not w-max) gives the table-fixed layout a definite width to
            fill, so the fill columns truncate instead of widening the table. */}
        <div className="w-full">
          {/* Radix unmounts the inactive tab, so each table fetches only its
              own data — the automation tab is the only one that loads automations. */}
          <TabsContent value="operating">
            <OperatingAccountsTable />
          </TabsContent>
          <TabsContent value="automation">
            <AutomationAccountsTable />
          </TabsContent>
          <TabsContent value="cards">
            <CardsPage />
          </TabsContent>
        </div>
      </PageLayout>
    </TabsPrimitive.Root>
  )
}

const OperatingAccountsTable = () => {
  const activeOrgId = useActiveOrgId()
  const { smartAccounts, isLoading: isLoadingAccounts } = useSmartAccounts({
    includeAutomations: true,
  })
  const { balances, isLoading: isLoadingBalances } = useOrgBalances(activeOrgId)

  const columns: AccountColumn[] = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        size: NAME_COLUMN_WIDTH,
        maxSize: NAME_COLUMN_WIDTH,
        cell: function NameCell(ctx) {
          return <AccountName account={ctx.row.original} />
        },
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'balance',
        header: 'Balance',
        meta: { align: 'right' },
        cell: function BalanceCell(ctx) {
          return (
            <AccountBalance
              account={ctx.row.original}
              smartAccountBalances={balances}
              isLoadingSmartAccountBalances={isLoadingBalances}
            />
          )
        },
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'rules',
        header: 'Signers',
        cell: function RulesCell(ctx) {
          return <AccountRules account={ctx.row.original} />
        },
        enableColumnFilter: false,
      }),
      accountActionsColumn,
    ],
    [balances, isLoadingBalances],
  )

  const accounts = useMemo(() => {
    const operatingRows: AccountRow[] = smartAccounts
      .filter(
        (account) =>
          getAccountType(account) === 'Operating' && !account.isArchived,
      )
      .map((account) => ({
        id: account.id,
        type: 'Operating' as const,
        name: account.name,
        address: account.address,
        account,
      }))

    return sortAccountRows(operatingRows, balances)
  }, [balances, smartAccounts])

  return (
    <AccountsTable
      columns={columns}
      accounts={accounts}
      isLoading={isLoadingAccounts}
      emptyMessage="Your workspace has no operating accounts"
    />
  )
}

const AutomationAccountsTable = () => {
  const activeOrgId = useActiveOrgId()
  const { smartAccounts, isLoading: isLoadingAccounts } = useSmartAccounts({
    includeAutomations: true,
  })
  const { balances } = useOrgBalances(activeOrgId)
  const { automations, isSuccess: isAutomationsSuccess } = useAutomations()

  const columns: AccountColumn[] = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        size: NAME_COLUMN_WIDTH,
        maxSize: NAME_COLUMN_WIDTH,
        cell: function NameCell(ctx) {
          return <AccountName account={ctx.row.original} />
        },
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'status',
        header: 'Status',
        size: 180,
        maxSize: 180,
        cell: function StatusCell(ctx) {
          return <AutomationAccountStatus account={ctx.row.original} />
        },
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'output',
        header: 'Output',
        cell: function OutputCell(ctx) {
          return <AccountRules account={ctx.row.original} />
        },
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'destination',
        header: 'Destination',
        cell: function DestinationCell(ctx) {
          return <AutomationAccountDestination account={ctx.row.original} />
        },
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'processed',
        header: 'Processed',
        meta: { align: 'right' },
        cell: function ProcessedCell(ctx) {
          return <AutomationAccountProcessed account={ctx.row.original} />
        },
        enableColumnFilter: false,
      }),
      accountActionsColumn,
    ],
    [],
  )

  const accounts = useMemo(() => {
    const automationByAddress = new Map(
      automations
        .filter((automation) => automation.accountAddress)
        .map((automation) => [
          automation.accountAddress!.toLowerCase(),
          automation,
        ]),
    )

    const accountNameByAddress = new Map(
      smartAccounts.map((account) => [
        account.address.toLowerCase(),
        account.role === SmartAccountRole.TREASURY ? 'Treasury' : account.name,
      ]),
    )

    const automationRows: AccountRow[] = smartAccounts
      .filter(
        (account) =>
          getAccountType(account) === 'Automation' && !account.isArchived,
      )
      .map((account) => {
        const automation = automationByAddress.get(
          account.address.toLowerCase(),
        )
        const automationDestinationName = automation
          ? accountNameByAddress.get(
              automation.policy.passThroughDestinationAddress.toLowerCase(),
            )
          : undefined

        return {
          id: account.id,
          type: 'Automation' as const,
          name: account.name,
          address: account.address,
          account,
          automation,
          automationDestinationName,
        }
      })

    return sortAccountRows(automationRows, balances)
  }, [automations, balances, smartAccounts])

  if (isAutomationsSuccess && !isLoadingAccounts && accounts.length === 0) {
    return (
      <FeatureEmptyState
        title="Automations"
        showTitle={false}
        paragraphs={[
          'Create a receiving address that swaps and routes incoming funds automatically. Use it to convert token revenue to USDC or set aside funds for taxes.',
        ]}
        learnMoreUrl={AUTOMATIONS_GUIDE_URL}
        createButton={<NewAutomationButton />}
      />
    )
  }

  return (
    <AccountsTable
      columns={columns}
      accounts={accounts}
      isLoading={isLoadingAccounts}
      emptyMessage="Your workspace has no automation accounts"
    />
  )
}

const AccountsTable = ({
  columns,
  accounts,
  isLoading,
  emptyMessage,
}: {
  columns: AccountColumn[]
  accounts: AccountRow[]
  isLoading: boolean
  emptyMessage: string
}) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: INITIAL_PAGE_SIZE,
  })

  return (
    <Table
      columns={columns}
      data={accounts}
      isLoading={isLoading}
      emptyMessage={emptyMessage}
      state={{ pagination }}
      onPaginationChange={setPagination}
      pagination={{
        enabled: true,
        initialPageSize: INITIAL_PAGE_SIZE,
      }}
      rowPath={(row) => `/accounts/${row.original.address}`}
    />
  )
}

const NewAutomationButton = () => {
  const { org } = useActiveOrg()
  const orgMember = useActiveOrgMember()
  const isNewButtonDisabled = orgMember?.role !== 'OWNER' || org?.isPersonal

  return (
    <Button
      isDisabled={isNewButtonDisabled}
      disabledReason="Only owners can create automations"
      href={{
        path: buildCreatePath({
          type: 'automation',
          returnTo: '/accounts?tab=automation',
        }),
      }}
    >
      Create
    </Button>
  )
}

const NewAccountButton = () => {
  const { org } = useActiveOrg()
  const orgMember = useActiveOrgMember()
  const searchParams = useSearchParams()
  const activeTab = getActiveAccountTab(searchParams)
  const isNewButtonDisabled = orgMember?.role !== 'OWNER' || org?.isPersonal
  const accountType = activeTab === 'automation' ? 'automation' : 'operating'

  return (
    <Button
      isDisabled={isNewButtonDisabled}
      disabledReason="Only owners can create accounts"
      href={{
        path: buildCreatePath({
          type: accountType,
          returnTo: `/accounts?tab=${activeTab}`,
        }),
      }}
    >
      Create account
    </Button>
  )
}

export const AccountsPage = withAuthentication(AccountsPageInner)
